import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import U2000SaveCancelDialog from '../../../u2000Core/components/dialog/U2000SaveCancelDialog';
import U2000ActionBtn from '../../../u2000Core/components/buttons/U2000ActionBtn';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import { useU2000LogicalUnitContext } from '../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { NS, U3146ROLES } from '../../U3146';
import { useU3146Context } from '../../U3146Context';
import {
  u3146CloseLineIsClosed,
  u3146IsValidInactiveDays,
  u3146NormalizeInactiveDaysDefault,
  u3146PreviewEligibleProjectIds,
  u3146PreviewLineIsEligible,
  type U3146CloseLineResult,
  type U3146PreviewLine,
  type U3146PreviewResult,
} from '../../U3146Service';
import { useU3146CloseInactiveWizardHandlers } from '../../U3146UseCloseInactiveWizardHandlers';
import U3146CloseInactiveWizardCloseResults from './U3146CloseInactiveWizardCloseResults';
import U3146CloseInactiveWizardConfirmStep from './U3146CloseInactiveWizardConfirmStep';
import U3146CloseInactiveWizardParamsPanel from './U3146CloseInactiveWizardParamsPanel';

type WizardStep = 1 | 2 | 'results';

/** Même shell horizontal que `U2000BasicTable` (liste U3121). */
const U3146_WIZARD_PAGE_SHELL = 'w-full px-4 py-2 sm:px-6 lg:px-8';

const U3146CloseInactiveWizardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const { initLU } = useU3146Context();
  const { preview, close, saveInactiveDaysDefault } = useU3146CloseInactiveWizardHandlers();

  const canClose = hasRole(U3146ROLES.CLOSE);
  const canEditDefault = hasRole(U3146ROLES.EDIT);

  const [step, setStep] = useState<WizardStep>(1);
  const [inactiveDays, setInactiveDays] = useState(() =>
    u3146NormalizeInactiveDaysDefault(initLU.personalization),
  );
  const [previewResult, setPreviewResult] = useState<U3146PreviewResult | null>(null);
  const [previewLines, setPreviewLines] = useState<U3146PreviewLine[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(() => new Set());
  const [closeResults, setCloseResults] = useState<U3146CloseLineResult[] | null>(null);
  const [loading, setLoading] = useState<'preview' | 'close' | 'saveDefault' | null>(null);
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);

  useEffect(() => {
    setInactiveDays(u3146NormalizeInactiveDaysDefault(initLU.personalization));
  }, [initLU.personalization]);

  useEffect(() => {
    setLogicalUnitBreadcrumb([
      { label: t('breadcrumbProjects', { ns: NS }), href: '/U3121' },
      { label: t('title', { ns: NS }) },
    ]);
    return () => setLogicalUnitBreadcrumb([]);
  }, [setLogicalUnitBreadcrumb, t]);

  const eligibleProjectIds = useMemo(() => u3146PreviewEligibleProjectIds(previewLines), [previewLines]);

  const selectedEligibleCount = useMemo(
    () => eligibleProjectIds.filter((id) => selectedProjectIds.has(id)).length,
    [eligibleProjectIds, selectedProjectIds],
  );

  const excludedCount = useMemo(
    () => previewLines.filter((line) => !u3146PreviewLineIsEligible(line)).length,
    [previewLines],
  );

  const writeOffCount = useMemo(
    () =>
      previewLines.filter(
        (l) => u3146PreviewLineIsEligible(l) && selectedProjectIds.has(l.projectId) && l.willCreateWriteOff,
      ).length,
    [previewLines, selectedProjectIds],
  );

  const openInvoiceCount = useMemo(
    () =>
      previewLines
        .filter((l) => u3146PreviewLineIsEligible(l) && selectedProjectIds.has(l.projectId))
        .reduce((sum, l) => sum + (l.status === 'eligible' ? (l.openStandardInvoiceCount ?? l.openStandardInvoices?.length ?? 0) : 0), 0),
    [previewLines, selectedProjectIds],
  );

  const handleToggleProject = useCallback((projectId: string, checked: boolean) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(projectId);
      else next.delete(projectId);
      return next;
    });
  }, []);

  const handleToggleAllEligible = useCallback(
    (checked: boolean, visibleEligibleProjectIds?: string[]) => {
      if (checked) {
        const ids = visibleEligibleProjectIds ?? eligibleProjectIds;
        setSelectedProjectIds(new Set(ids));
      } else {
        setSelectedProjectIds(new Set());
      }
    },
    [eligibleProjectIds],
  );

  const resetWizard = useCallback(() => {
    setStep(1);
    setPreviewResult(null);
    setPreviewLines([]);
    setSelectedProjectIds(new Set());
    setCloseResults(null);
    setLoading(null);
    setConfirmArchiveOpen(false);
    setInactiveDays(u3146NormalizeInactiveDaysDefault(initLU.personalization));
  }, [initLU.personalization]);

  const handleBackToList = useCallback(() => {
    navigate('/U3121');
  }, [navigate]);

  const handleContinue = useCallback(async () => {
    if (!u3146IsValidInactiveDays(inactiveDays)) {
      toast.error(t('errInactiveDaysInvalid', { ns: NS }));
      return;
    }
    setLoading('preview');
    const result = await preview({ inactiveDays });
    setLoading(null);
    if (!result) return;
    setPreviewResult(result);
    setPreviewLines(result.lines);
    setSelectedProjectIds(new Set(u3146PreviewEligibleProjectIds(result.lines)));
    setStep(2);
  }, [inactiveDays, preview, toast, t]);

  const handleSaveDefault = useCallback(async () => {
    if (!canEditDefault) return;
    setLoading('saveDefault');
    const ok = await saveInactiveDaysDefault(inactiveDays);
    setLoading(null);
    if (ok) {
      setInactiveDays(inactiveDays);
    }
  }, [canEditDefault, inactiveDays, saveInactiveDaysDefault]);

  const runClose = useCallback(async () => {
    const projectIds = eligibleProjectIds.filter((id) => selectedProjectIds.has(id));
    if (projectIds.length === 0) {
      toast.error(t('errWizardNoProjectsSelected', { ns: NS }));
      return;
    }
    if (!canClose) {
      toast.error(t('errWizardCloseRequiresRole', { ns: NS }));
      return;
    }
    setLoading('close');
    const result = await close({
      inactiveDays,
      saveInactiveDaysAsDefault: false,
      projectIds,
    });
    setLoading(null);
    if (!result) return;

    const closed = result.lines.filter(u3146CloseLineIsClosed).length;
    const errors = result.lines.filter((l: U3146CloseLineResult) => l.status === 'error').length;
    if (errors === 0) {
      toast.success(t('msgCloseSuccess', { ns: NS, count: closed }));
    } else {
      toast.warning(t('msgClosePartial', { ns: NS }));
    }
    setCloseResults(result.lines);
    setStep('results');
  }, [eligibleProjectIds, selectedProjectIds, canClose, close, inactiveDays, toast, t]);

  const handleArchiveClick = useCallback(() => {
    setConfirmArchiveOpen(true);
  }, []);

  const isBusy = loading !== null;

  return (
    <main
      className={`${U3146_WIZARD_PAGE_SHELL} flex min-w-0 flex-col ${
        step === 2 ? 'min-h-0 flex-1 gap-4 overflow-hidden' : 'gap-6'
      }`}
    >
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 border-b border-gray-200 pb-4">
        <nav aria-label={t('wizardStepsAria', { ns: NS })}>
          <ol className="m-0 flex list-none flex-wrap items-center gap-2 p-0 text-sm">
            <li
              aria-current={step === 1 ? 'step' : undefined}
              className={
                step === 1
                  ? 'font-semibold text-blue-700'
                  : step === 2 || step === 'results'
                    ? 'text-gray-600'
                    : 'text-gray-400'
              }
            >
              <span className="mr-1" aria-hidden>
                {step === 2 || step === 'results' ? '✓' : '1.'}
              </span>
              {t('wizardStep1Title', { ns: NS })}
            </li>
            <li className="text-gray-300" aria-hidden>
              →
            </li>
            <li
              aria-current={step === 2 ? 'step' : undefined}
              className={
                step === 2
                  ? 'font-semibold text-blue-700'
                  : step === 'results'
                    ? 'text-gray-600'
                    : 'text-gray-400'
              }
            >
              <span className="mr-1" aria-hidden>
                {step === 'results' ? '✓' : '2.'}
              </span>
              {t('wizardStep2Title', { ns: NS })}
            </li>
            {step === 'results' && (
              <>
                <li className="text-gray-300" aria-hidden>
                  →
                </li>
                <li aria-current="step" className="font-semibold text-blue-700">
                  <span className="mr-1" aria-hidden>
                    3.
                  </span>
                  {t('wizardStep3Title', { ns: NS })}
                </li>
              </>
            )}
          </ol>
        </nav>
        <U2000ActionBtn label={t('wizardBackToProjectList', { ns: NS })} handleClick={handleBackToList} />
      </div>

      {step === 1 && (
        <section className="flex w-full min-w-0 flex-col gap-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900">{t('wizardStep1Title', { ns: NS })}</h2>
            <p className="mt-1 text-sm text-gray-600">{t('wizardStep1Help', { ns: NS })}</p>
          </div>
          <article className="form-card animate-fade-in w-full">
            <div className="form-card-body px-4 py-2">
              <U3146CloseInactiveWizardParamsPanel
                inactiveDays={inactiveDays}
                onInactiveDaysChange={setInactiveDays}
                onSaveDefault={canEditDefault ? () => void handleSaveDefault() : undefined}
                onContinue={() => void handleContinue()}
                canSaveDefault={canEditDefault}
                loadingPreview={loading === 'preview'}
                loadingSaveDefault={loading === 'saveDefault'}
                disabled={isBusy}
              />
            </div>
          </article>
        </section>
      )}

      {step === 2 && previewResult && (
        <article className="form-card animate-fade-in flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
          <section className="form-card-body mb-2 flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-2">
            <U3146CloseInactiveWizardConfirmStep
              lines={previewLines}
              selectedProjectIds={selectedProjectIds}
              onToggleProject={handleToggleProject}
              onToggleAllEligible={handleToggleAllEligible}
              selectedEligibleCount={selectedEligibleCount}
              excludedCount={excludedCount}
              writeOffCount={writeOffCount}
              openInvoiceCount={openInvoiceCount}
              canClose={canClose}
              onBack={() => setStep(1)}
              onArchive={handleArchiveClick}
              archiving={loading === 'close'}
            />
          </section>
        </article>
      )}

      {step === 'results' && closeResults && (
        <article className="form-card animate-fade-in w-full">
          <div className="form-card-body px-4 py-2">
            <U3146CloseInactiveWizardCloseResults
              results={closeResults}
              onBackToList={handleBackToList}
              onNewSession={resetWizard}
            />
          </div>
        </article>
      )}

      <U2000SaveCancelDialog
        isOpen={confirmArchiveOpen}
        title={t('wizardArchiveConfirmTitle', { ns: NS })}
        onClose={(action: string) => {
          setConfirmArchiveOpen(false);
          if (action === 'save') {
            void runClose();
          }
        }}
        width="medium"
      >
        <p className="text-sm text-gray-700">{t('wizardArchiveConfirmMessage', { ns: NS, count: selectedEligibleCount })}</p>
      </U2000SaveCancelDialog>
    </main>
  );
};

export default U3146CloseInactiveWizardPage;
